'use client';

import type { News } from '@/types';

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';

import BoxImage from '@/app/(pages)/_components/Blog/BoxImage';
import { Button, Center, Container, SimpleGrid } from '@/components';
import { useGetNews, useTranslation } from '@/hooks';

const AllNews: React.FC<{ data: News[] }> = ({ data }) => {
  const { t } = useTranslation('pages.blogs');
  const [allNews, setAllNews] = useState<News[]>(data || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState<number | null>(null);
  const { mutateAsync: loadNews } = useGetNews();
  const searchParams = useSearchParams();
  const sortBy = searchParams.get('sortBy');
  const [readMoreLoading, setReadMoreLoading] = useState(false);

  const readMore = () => {
    const newPageNumber = currentPage + 1;
    setReadMoreLoading(true);
    setCurrentPage(newPageNumber);
    if (newPageNumber === 1) {
      return;
    }
    const fetchData = async () => {
      try {
        const res = await loadNews({
          current: newPageNumber,
          pageSize: 10,
          sortBy:
            (sortBy === 'is_most_viewed' && 'is_most_viewed') ||
            (sortBy === 'is_most_rated' && 'is_most_rated') ||
            'newest',
        });
        setAllNews((prev) => [...prev, ...(res?.list || [])]);
        setTotalItems(res.pagination.total);
      } catch {
      } finally {
        setReadMoreLoading(false);
      }
    };

    void fetchData();
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await loadNews({
          current: 1,
          pageSize: 10,
          sortBy:
            (sortBy === 'is_most_viewed' && 'is_most_viewed') ||
            (sortBy === 'is_most_rated' && 'is_most_rated') ||
            'newest',
        });
        setAllNews(res?.list || []);
        setTotalItems(res.pagination.total);
        setCurrentPage(1);
      } catch {}
    };

    void fetchData();
  }, [sortBy]);

  return (
    <>
      <Container>
        <SimpleGrid columns={{ base: 1, md: 3, xl: 5 }} gap={6}>
          {allNews.map((item) => (
            <BoxImage
              image={item.preview_image}
              alt={item.alt_image}
              code={item.code}
              title={item.title}
              key={item.id}
            />
          ))}
        </SimpleGrid>
      </Container>
      {(totalItems === null || allNews.length < totalItems) && (
        <Center mt="44px">
          <Button
            variant="outline"
            onClick={readMore}
            isLoading={readMoreLoading}
          >
            {t('readMore')}
          </Button>
        </Center>
      )}
    </>
  );
};

export default AllNews;
